/**
 * DependencyChainView component for visualizing full dependency chains.
 * 
 * Shows a collapsible tree view of all tasks that must be completed
 * before the current task, using recursive dependency chain data.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { DependencyChainResponse, getDependencyChain } from '../api/task-dependencies';
import { Task } from '../types/task';
import { getTasks } from '../api/tasks';
import Badge from './Badge';

interface DependencyChainViewProps {
  taskId: string;
  taskTitle: string;
}

const DependencyChainView: React.FC<DependencyChainViewProps> = ({
  taskId,
  taskTitle,
}) => {
  const [chain, setChain] = useState<DependencyChainResponse | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const loadDependencyChain = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [chainData, allTasks] = await Promise.all([
        getDependencyChain(taskId),
        getTasks()
      ]);
      setChain(chainData);
      setTasks(allTasks);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dependency chain';
      setError(errorMessage);
      setChain(null);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadDependencyChain();
  }, [loadDependencyChain]);

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case 'completed':
        return '✓';
      case 'in_progress':
        return '▶';
      case 'todo':
        return '○';
      default:
        return '○';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="text-gray-700 dark:text-gray-300">Loading dependency chain...</p>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100">{error}</div>;
  }

  if (!chain || (chain.transitive_predecessors.length === 0 && chain.blocking_tasks.length === 0)) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        No dependencies for this task.
      </div>
    );
  }

  // Helper to get task details by ID
  const getTaskById = (id: string): Task | undefined => {
    return tasks.find(t => t.id === id);
  };

  const predecessorTasks = chain.transitive_predecessors.map(getTaskById).filter(Boolean) as Task[];
  const blockingTasks = chain.blocking_tasks.map(getTaskById).filter(Boolean) as Task[];

  return (
    <div className="dependency-chain-view">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">Dependency Chain</h4>
        {predecessorTasks.length > 1 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            {isExpanded ? 'Collapse' : 'Expand All'}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {/* Predecessor tasks (tasks this depends on) */}
        {predecessorTasks.length > 0 && (
          <>
            <div className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2">
              Prerequisites ({predecessorTasks.length}):
            </div>
            {predecessorTasks.slice(0, isExpanded ? undefined : 3).map(task => (
              <div key={task.id} className="flex items-start gap-3">
                <div className={`flex-1 p-3 border rounded-lg transition-all hover:shadow-md ${
                  task.status === 'completed' 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                    : task.status === 'in_progress'
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-300 dark:border-gray-700'
                }`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-lg">{getStatusIcon(task.status)}</span>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 dark:text-gray-100">{task.title}</div>
                      </div>
                    </div>
                    <Badge status={task.status as any} size="sm">
                      {task.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
            {!isExpanded && predecessorTasks.length > 3 && (
              <div className="text-xs text-gray-500 dark:text-gray-400 pl-3">
                + {predecessorTasks.length - 3} more...
              </div>
            )}
          </>
        )}

        {/* Arrow pointing to current task */}
        {predecessorTasks.length > 0 && (
          <div className="flex items-center gap-3 pl-6">
            <div className="text-gray-400 dark:text-gray-600">↓</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">required for</div>
          </div>
        )}

        {/* Current task (level 0) */}
        <div className="flex items-start gap-3">
          <div className={`flex-1 p-3 border-2 rounded-lg border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20`}>
            <div className="flex items-center gap-2">
              <span className="text-lg">🎯</span>
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100">{taskTitle}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Current Task</div>
              </div>
            </div>
          </div>
        </div>

        {/* Arrow pointing to blocking tasks */}
        {blockingTasks.length > 0 && (
          <div className="flex items-center gap-3 pl-6">
            <div className="text-gray-400 dark:text-gray-600">↓</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">blocks</div>
          </div>
        )}

        {/* Blocking tasks (tasks that depend on this) */}
        {blockingTasks.length > 0 && (
          <>
            <div className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2">
              Blocking ({blockingTasks.length}):
            </div>
            {blockingTasks.map(task => (
              <div key={task.id} className="flex items-start gap-3">
                <div className={`flex-1 p-3 border rounded-lg bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⚠️</span>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">{task.title}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        Waiting on current task
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Legend */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">Status Legend:</div>
          <div className="flex flex-wrap gap-3 text-xs text-gray-700 dark:text-gray-300">
            <div className="flex items-center gap-1">
              <span className="text-green-600 dark:text-green-400">✓</span>
              <span>Completed</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-blue-600 dark:text-blue-400">▶</span>
              <span>In Progress</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-600 dark:text-gray-400">○</span>
              <span>To Do</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DependencyChainView;
